// VRG-133: pre-launch signup gate — Waitlist (approve → Allow / Allow & invite) + Allowlist
// (emails permitted to self-sign-up). Moved here from the Internal Users page. platform_admin-only.
//
// VRG-401: promoted from a Labs SECTION (`/internal/labs/signups`, exported as `SignupsLab`)
// to a first-class internal page at `/internal/signups`, with its own sidebar row in the
// accounts group. Labs holds prototypes and design decisions; this is a live operational
// queue that a person works down, and one buried two levels behind a flask icon is one
// nobody checks. §8's test agrees: staff operate on signups directly — approve, reject,
// invite, comp, grant — across instances, so it is a first-class object, not a facet of Labs.
//
// The BODY below is unchanged by that move. Only the export name, the page Header and the
// admin guard are new, so this file's diff stays reviewable as a rename.
import React, { useCallback, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { UserPlusIcon, KeyRoundIcon, ArrowUpRightIcon, CircleXIcon, CircleCheckIcon, MailIcon, Trash2Icon, GiftIcon, CoinsIcon, DollarSignIcon, XIcon } from 'lucide-react'
import { Button, EMPTY_VALUE, Tabs, TABS_CONTENT_GAP } from '@repo/ui'

import { ROUTES, CLASSIC_ACCESS_TITLE } from 'routes'
// VRG-545 — `CompanyCell` and `SubscriptionCell` are the SHARED reference cells
// (§36/§28), not page-local previews: both already carry `useReferrerState`, so the
// company / billing page each lands on can offer "← Back to Signups" without this
// page wiring anything (§42).
import { Header, Table, EmptyState, StatusChip, ActionDropdown, GrantSignupModal, AddToAllowlistModal, CompSubscriptionModal, GrantActivationsModal, CompanyCell, SubscriptionCell, headerWithInfo, ConfirmSettingModal, EmailPreview } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { SIGNUP_STATUSES, SIGNUP_CONVERSION_STATUSES, SIGNUP_PRICING_ACCESS_STATUSES, SIGNUP_TRACKING_STATUSES } from 'constants/statuses'
import { date, buildExternalLink, openExternalLink, EXTERNAL_LINK_BASES } from 'utils'
import { useCanAccess, useDisclosure } from 'hooks'
import { useSignups, pricingAccessIsLive, type SignupEntry } from 'hooks/api/useSignups'
// VRG-Pricing.Access.Gate — a grant SEPARATE from platform access; see the hook file.
import { useGrantPricingAccess, useRevokePricingAccess } from 'hooks/api/usePricingAccess'
// VRG-Pricing.Access.Gate — the subject a gate previews, read from the email
// registry so it cannot disagree with the one the send delivers.
import { useEmailTemplateSubject } from 'hooks/api/useEmailTemplateSubject'
import { useGrantSignup, useGrantSignupByEmail } from 'hooks/api/useGrantSignup'
// VRG-545 — the plan catalog, for the potential-revenue column. It supplies BOTH halves of
// that figure: each plan's monthly `amount` and the `activation_tiers` schedule synced out
// of the Stripe metered price. There is no hardcoded ladder in this repo to fall back on,
// which is deliberate (see `potentialRevenue.ts`).
import { useSubscriptionPlans } from 'hooks/api/useSubscription'
import { useRejectSignup } from 'hooks/api/useRejectSignup'
import { useSignupAllowlist, type SignupAllowlistEntry } from 'hooks/api/useSignupAllowlist'
import { useSnackbar } from 'contexts'
// One directory shallower than before the VRG-401 move (was `../../CompanyPage/...`
// from `LabsPage/sections/`); this file now sits directly under `pages/internal/`.
import { BRAND_ICONS } from '../CompanyPage/brand-icons'
// VRG-545 — one grid column per field the marketing site forwards, GENERATED from the
// `WEBSITE_FIELDS` manifest. A panel is neither sortable nor filterable, and qualifying a
// lead means comparing figures across rows; see that file's header for why the columns are
// derived rather than written out.
import { buildWebsiteColumns } from './websiteColumns'
// The request-access form's newsletter opt-in and privacy-notice record (2026-10-08).
import { buildConsentColumns } from './consentColumns'
import { buildFirstTouchColumn } from './firstTouchColumn'
// VRG-545 — a DERIVED column, deliberately not a `WEBSITE_FIELDS` entry: the manifest is the
// contract with vera-website and its drift guard reads that repo's source, so a computed
// figure the site has never heard of does not belong in it. See that file's header.
import { buildPotentialRevenueColumn } from './potentialRevenueColumn'
// VRG-545 — the second derived column, same reasoning. ⚠️ Its money is in the ROW's currency
// while `potentialRevenueColumn`'s is in USD; each inherits the currency of what it is built
// from. See `brokerageRevenue.ts` for why, and for why Vera's 2.5% fee is NOT `brokerTake`.
import { buildBrokerageRevenueColumn } from './brokerageRevenueColumn'
// VRG-545 — the "ROI submitted" column, extracted here when it stopped being four lines of
// boolean and became a count with its own provenance tooltip. Same sibling pattern as the
// two revenue columns above; the counting itself lives in `websiteValues.ts` beside the rest
// of the row-reading, so the cell and the CSV export cannot end up with two answers.
import { buildRoiInputsColumn } from './roiInputsColumn'
// Site-gate invites: per-person links past vera.inc's pre-launch password gate. Its own
// file because it shares nothing with the two tabs above but the page.
import { SiteInvitesTab } from './SiteInvitesTab'
// VRG-658 — the waitlist's "Referred by" cell. The Referrals tab moved to GTM → Affiliates →
// Refer & earn (VRG-684); `?tab=referrals` here redirects there (App.tsx).
import { ReferredByBlock } from './referrals/StaffReferralsTable'
import { referredByOf } from './referrals/referralLabels'

const cellFlexSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * VRG-Pricing.Access.Gate — can this person see Vera's price list, EFFECTIVELY?
 *
 * Founder directive, 2026-09-22: *"if the user is already allowed to sign up, then
 * they should already have access to the pricing page."* This is that implication,
 * written down once so the pill and the kebab cannot disagree about it.
 *
 * ## It is not a UI convenience — the SERVER already behaves this way
 *
 * `GrantSignupUseCase` stamps `grantPricingAccess` as its last step (that file's
 * "Allow ⟹ pricing access, and ONLY in that direction" section), so every Allow
 * performed since that landed writes the pricing columns too. And independently of
 * the columns, `GET /api/pricing-access/start` mints a handoff code for ANY visitor
 * holding a platform session, consulting this row not at all — which is exactly what
 * "allowed to sign up" buys someone. So the implication is the product's behaviour;
 * this predicate only stops the grid describing it differently.
 *
 * ## ⚠️ THE RULING ON `status: 'granted'` + a stored `pricing_access_revoked_at`
 *
 * That combination is REACHABLE in data — a row allowed, then pricing-revoked — and
 * it exists in screenshots. This predicate deliberately reads it as **Granted**, and
 * the stored revocation is left in the database untouched as history. Three reasons,
 * and the third is the one that decided it:
 *
 *  1. **It is what the person experiences.** Revocation's own use case lists what it
 *     cannot reach, and the second item is "a `handoff` token's route … does not
 *     consult the signup row at all". An allowed person signs in and reads the price
 *     list. A column that answered "Revoked" would be describing a COLUMN, not the
 *     capability the operator is asking about.
 *  2. **The row is already internally inconsistent, and something has to win.** The
 *     alternative is a pill that says Revoked beside a Signup-allowed pill that says
 *     Allowed, leaving the operator to know this file's implication in order to read
 *     their own table.
 *  3. **Showing "Revoked" would be a dead end (§46).** Under this change both pricing
 *     actions hide on an allowed row, so a Revoked pill would name a state with no
 *     action beside it and no explanation. §46 permits hiding only when the RESOLVING
 *     action stays reachable, and here it is "Remove signup access" on the same menu:
 *     withdraw the larger grant and the implication goes with it.
 *
 * The honesty cost is paid by the TOOLTIP rather than by the pill — see
 * `impliedPricingAccessNote`, which says where the Granted came from and says so
 * explicitly when a stale revocation is on the record.
 *
 * ⚠️ Going forward the state is unreachable FROM THIS PAGE only. `DELETE
 * /internal/signups/:id/pricing-access` still accepts an allowed row, so a direct API
 * call can still create one. That is why this reads the contradiction rather than
 * assuming it away.
 */
const signupImpliesPricingAccess = (row: SignupEntry): boolean =>
  row.status === 'granted' || pricingAccessIsLive(row)

/**
 * Why an ALLOWED row's Pricing access pill says Granted, in the operator's terms.
 *
 * Two sentences at most, no em-dashes (§15), and the second one exists only when
 * there is a stored revocation to account for. Without that branch the pill would
 * silently overrule a decision an admin actually made, which is the one thing the
 * effective-truth reading must not do quietly.
 */
const impliedPricingAccessNote = (row: SignupEntry): string =>
  row.pricing_access_revoked_at
    ? 'Signup access includes pricing access, so this person can see the price list once they sign in. An earlier pricing revocation is still on the record but no longer applies.'
    : 'Signup access includes pricing access, so this person can see the price list once they sign in.'

/**
 * VRG-Pricing.Access.Gate — widen a confirm gate that carries a rendered email.
 *
 * `Modal`'s own 533px is right for a paragraph and an alert. The email card is
 * 600px (`base-email.tsx`), and at 533 the modal's 16px padding leaves ~501, so
 * the card reflows and the operator reviews a narrower message than the one
 * being sent. 680 gives the body 648.
 *
 * ⚠️ NOT a fixed pixel floor: `Modal` keeps `maxWidth: calc(100vw - 32px)`
 * underneath, so a narrow viewport shrinks the dialog and the email reflows
 * INSIDE it rather than scrolling sideways (§74). The frame is `width: 100%`
 * for that reason; see `EmailPreview`.
 */
const EMAIL_GATE_MODAL_SX = { width: 680 }

/**
 * The "what they will receive" block inside a confirm gate: the subject line,
 * the rendered message, and the one caveat the render cannot carry.
 *
 * Local to this page because all three of its uses are on it, and §36's trigger
 * is the SECOND hand-roll, not the first speculative extraction. The reusable
 * half — fetching and framing the real template — is already shared
 * (`components/display/EmailPreview`); what is page-shaped is this particular
 * label / preview / caveat arrangement.
 *
 * `note` is required rather than optional on purpose. Every preview here renders
 * a SAMPLE link, because the real one is a credential minted at send time, and a
 * preview that shows a placeholder URL with nothing saying so is the drift this
 * whole block exists to avoid: it reads as the address the recipient will get.
 *
 * ## There is NO `subject` prop, and that absence is the point
 *
 * There was one, and it drifted within days. The body came from the real
 * template and could not go stale; the subject was a string literal at the call
 * site, so when the delivered subject changed to "You have been granted access
 * to view Vera pricing" the modal carried on previewing "Your Vera pricing" —
 * under a comment asserting there was no second copy to keep in step. Half of a
 * message resolved from the source and half retyped is not a preview, it is a
 * preview plus a claim, and only one of them can be wrong silently.
 *
 * Both halves now resolve from `packages/emails`' registry: the body through
 * `renderEmail`, the subject through the Labs index
 * (`useEmailTemplateSubject`). The registry subject is also what the SEND uses —
 * `sendPricingAccessLink` reads it via `getEmailSubject` — so what this line
 * shows is what Mailpit receives, provably rather than by convention
 * (`pricing-access-subject.guard.spec.ts`).
 */
const EmailGatePreview = ({ templateId, note }: { templateId: string; note: string }) => {
  const { subject } = useEmailTemplateSubject(templateId)

  return (
    <Stack spacing={2} sx={{ mt: 4 }}>
      <Typography variant="body3" color="text.secondary">
        {/*
          An em-dash placeholder while the index loads, never a guessed string.
          A literal default here would be the removed prop wearing a different
          name, and it would be WRONG in exactly the case that matters — the one
          where the registry has since changed.
        */}
        They receive this email. Subject: <strong>{subject ?? '—'}</strong>
      </Typography>
      <EmailPreview templateId={templateId} height={320} />
      {/* §15 — descriptive copy gets line-height so a two-clause caveat does not read dense. */}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {note}
      </Typography>
    </Stack>
  )
}

/**
 * "14 people" / "this person" — the subject of a gate that may cover one row or many.
 *
 * A bulk gate's load-bearing fact is the COUNT (§43: name the actual effect), and the
 * commonest way that goes wrong is a template that reads "Allow 1 people". So the
 * singular case is not a pluralised number at all — it is the demonstrative, which is
 * also what the single-row gates already say, so a shared modal reads naturally from
 * both entry points.
 */
const countPhrase = (n: number, singular = 'this person', plural = 'people') =>
  n === 1 ? singular : `${n} ${plural}`

/**
 * Open a bulk gate only when something is actually selected.
 *
 * ⚠️ Not defensive padding — an empty selection is REACHABLE today. The bulk bar
 * survives a tab switch while the selection behind it does not: select rows on
 * Waitlist, switch to Allowlist, and the bar is still there offering actions over
 * zero rows (verified in the browser — the grid reported 0 checked while the bar
 * still rendered). Before these gates that produced a silent no-op, so nobody saw
 * it. The gates made it visible as "Send the signup link to 0 people?", which is a
 * better failure than the silence but still not one to ship.
 *
 * So the gate declines to open and the click is inert, matching the prior
 * behaviour rather than inventing a new error state for a bar that should not have
 * been interactive. The stale bar itself is a Table-level concern and is left
 * alone deliberately: fixing selection lifetime across tabs is not this change's
 * to make, and doing it here would hide the symptom in one page while every other
 * tabbed list kept it.
 */
const openBulkGate = <T,>(rows: T[], open: (rows: T[]) => void) => {
  if (rows.length > 0) open(rows)
}

/**
 * WHO a bulk action is about to affect, listed rather than merely counted.
 *
 * ## Why a list and not just the number
 *
 * A count confirms the SCALE and nothing else, and the mistake a bulk gate exists to
 * catch is not "I meant 3, not 14" — it is "one of those 14 is not who I think it is".
 * An operator who mis-shift-clicked a row cannot discover that from a number, and for
 * an action that emails people and cannot be recalled, the addresses are the thing
 * worth checking. So the gate shows them.
 *
 * ## Why it is capped and scrolls
 *
 * `maxHeight` with `overflowY: auto` — the page size is 100, so an unbounded list
 * would push the confirm button off a 800px viewport and produce a modal whose action
 * you have to scroll to reach. Vertical scroll inside a modal is fine and expected;
 * §74 bars only the HORIZONTAL axis, which is why each address also gets
 * `overflowWrap: 'anywhere'` — a long address must wrap rather than widen the dialog.
 *
 * Rendered for a single recipient too. One address in the same frame as fourteen is
 * one fewer shape for a reader to learn, and it keeps the single-row and bulk gates
 * visibly the same control.
 */
const RecipientList = ({ emails }: { emails: string[] }) => (
  <Stack
    spacing={1}
    sx={{
      mt: 3,
      maxHeight: 168,
      overflowY: 'auto',
      // §74 — the modal itself must never scroll sideways, so a long address wraps.
      overflowX: 'hidden',
    }}
  >
    {emails.map((email) => (
      <Typography
        key={email}
        variant="body3"
        color="text.secondary"
        sx={{ overflowWrap: 'anywhere' }}
      >
        {email}
      </Typography>
    ))}
  </Stack>
)

/**
 * VRG-545 — one action on an allowlisted address, shared by the row kebab and the bulk bar.
 *
 * Structurally an `ActionDropdownItem` plus `bulkSafe`. Written out rather than extending
 * that type because `bulkSafe` is the whole reason the shape is named: it records WHY an
 * action is missing from the bulk bar, at the definition, instead of leaving a reader of the
 * bulk list to wonder whether the omission was deliberate.
 */
type AllowlistAction = {
  label: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  tooltip?: string
  destructive?: boolean
  divider?: boolean
  /**
   * Whether fanning this action out over a SELECTION does what its label says.
   *
   * `false` for anything that opens a modal about one address and has no `bulkOnClick`
   * below: the modal takes a single `email`, so a bulk entry would act on one row while
   * appearing to act on all of them.
   */
  bulkSafe: boolean
  /**
   * How this action behaves over a SELECTION, when repeating `onClick` per row would not.
   *
   * ⚠️ This exists because gating an action breaks the generic fan-out, silently. The bulk
   * bar derives its items by calling `allowlistActions(row.email)[index].onClick()` for
   * every selected row — correct while `onClick` MUTATES, and wrong the moment it opens a
   * modal instead: N `setState` calls collapse to one, the last address wins, and the gate
   * confirms a single row while the operator believes they are confirming all of them.
   * That is precisely the "acts on one while appearing to act on all" failure `bulkSafe`
   * was written to prevent, arriving through the front door.
   *
   * So a gated action supplies this, receiving every selected address at once and opening
   * ONE gate that names the whole set.
   */
  bulkOnClick?: (emails: string[]) => void
}
const TABS = [
  { label: 'Waitlist', value: 'waitlist' },
  { label: 'Allowlist', value: 'allowlist' },
  // The earliest door of the three: who may see vera.inc at all before launch.
  { label: 'Site invites', value: 'invites' },
]
const TAB_VALUES = TABS.map((t) => t.value)

export const SignupsPage = () => {
  const { open: notification } = useSnackbar()
  // platform_admin-only, exactly as it was inside Labs — LabsPage guards itself on the
  // same role and this surface left that guard behind when it moved out. `InternalRoute`
  // also admits platform_manager, so without this the promotion would have quietly WIDENED
  // who can approve a signup, which is not a nav change.
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  // `?tab=` seeds the initial tab so a link can open Site invites directly; the tab is
  // still local state afterwards, as before.
  const [tab, setTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab')
    return requested && TAB_VALUES.includes(requested) ? requested : 'waitlist'
  })
  const [isCreateInviteOpen, { open: openCreateInvite, close: closeCreateInvite }] = useDisclosure(false)
  const [isGrantModalOpen, { open: openGrantModal, close: closeGrantModal }] = useDisclosure(false)

  // ── Waitlist ────────────────────────────────────────────────────────────────
  const { signups, isLoading: isSignupsLoading, refetch: refetchSignups } = useSignups()

  // VRG-545 — the plan catalog behind the potential-revenue column. Not gated on the tab:
  // the query is cached app-wide (`staleTime: 60_000`) and shared with Billing, so fetching
  // it here costs nothing on a page a platform_admin is likely to reach billing from anyway.
  const { data: plans = [] } = useSubscriptionPlans()

  const { mutate: grantSignup } = useGrantSignup({
    onSuccess: (_data, vars) =>
      notification({ type: 'success', title: vars.invite ? 'Allowed & signup link sent' : 'Allowed to sign up' }),
    onError: (error: any) => notification({ type: 'error', title: error?.message || 'Failed to grant access' }),
  })

  const { mutate: rejectSignup } = useRejectSignup({
    // No success toast: the row's status chip flips on invalidation.
    onError: (error: any) => notification({ type: 'error', title: error?.message || 'Failed to reject signup' }),
  })

  /*
    VRG-Pricing.Access.Gate — access to the gated /pricing page on the marketing
    site. A DIFFERENT grant from Allow / Allow & invite above: those decide
    whether someone may create a Vera account, these decide whether they may
    read Vera's price list. Independent on purpose, so revoking one is not the
    other.

    Grant DOES get a success toast, unlike Reject. Reject's outcome is visible —
    the status chip flips — whereas the thing that matters here happened in
    somebody else's inbox, and the operator has no other way to learn it worked.

    Errors are surfaced rather than swallowed: the API stamps the grant and THEN
    emails, rethrowing on a send failure, so an error means "granted, no link
    sent" (usually PRICING_SITE_URL unset). Retrying is safe and idempotent.
  */
  const { mutate: grantPricingAccess } = useGrantPricingAccess({
    onSuccess: () => notification({ type: 'success', title: 'Pricing link sent' }),
    onError: (error: any) =>
      notification({ type: 'error', title: error?.message || 'Failed to grant pricing access' }),
  })

  const { mutate: revokePricingAccess } = useRevokePricingAccess({
    /*
      No success toast: the Pricing access column flips on invalidation.

      That sentence is load-bearing and was, for a while, the ONLY thing on this page
      still true about invalidation. Revoke passes `onError` alone, so the hook's
      `...options` spread had nothing to overwrite and its `invalidateQueries` survived;
      Grant passes a toast, and the same spread deleted its invalidation. Measured in the
      browser: the DELETE was followed by `GET /internal/signups`, the POST was not.

      ⚠️ So adding a success toast here is not a free change. It is free NOW, because the
      hook spreads options before its handlers (see `usePricingAccess.ts`), and it would
      have silently broken this column before that fix. If this column ever stops flipping,
      check the handler ordering in the hook before anything on this page.
    */
    onError: (error: any) =>
      notification({ type: 'error', title: error?.message || 'Failed to revoke pricing access' }),
  })

  /*
    VRG-Pricing.Access.Gate — the three row actions that reach OUTSIDE this
    console are gated, each holding the row it was opened on.

    All three change what a person outside Vera can do, and two of them put a
    message in that person's inbox. §43's test is whether the effect is
    downstream or hard to reverse, and "an email has been sent to a prospect"
    is both: nothing in this app can un-send it.

    The row, not just its id, because the modal has to NAME the recipient — a
    gate that says "this emails them" without saying who is a gate you can pass
    on the wrong row. Cleared on confirm and on cancel; `!!target` is the open
    flag, so there is one piece of state per gate rather than a boolean and an
    id that can disagree.

    ⚠️ Revoke pricing access and Reject are NOT gated. Revoke sends nothing and
    is one click from being undone. Reject already had no gate and adding one is
    not this ticket's to decide.
  */
  const [pricingGrantTarget, setPricingGrantTarget] = useState<SignupEntry | null>(null)
  const [allowTarget, setAllowTarget] = useState<SignupEntry | null>(null)
  const [inviteTarget, setInviteTarget] = useState<SignupEntry | null>(null)

  /*
    The BULK counterparts of the three gates above, plus the two allowlist actions.

    Separate state rather than widening `allowTarget` to `SignupEntry | SignupEntry[]`:
    the two gates ask DIFFERENT questions and therefore carry different copy (one
    names a person, the other a count), so collapsing them would mean a modal that
    branches its own text on the shape of its target — which is how a count gate ends
    up reading "Allow 1 people".

    ⚠️ The asymmetry these close was the actual defect: the single-row actions opened a
    confirm while the bulk bar fired `grantSignup` for every selected row immediately.
    The RISKIER action was the unguarded one — one click, N emails, no recall (§43).
  */
  const [bulkAllowTarget, setBulkAllowTarget] = useState<SignupEntry[] | null>(null)
  const [bulkInviteTarget, setBulkInviteTarget] = useState<SignupEntry[] | null>(null)
  /*
    The bulk pricing grant, and the ONE gate on this page whose target is not just a
    list of rows.

    It carries `skipped` because the rows it acts on are a SUBSET of the rows the
    operator selected: an allowed person already has pricing access (see
    `signupImpliesPricingAccess`), so granting again would only mail them a magic link
    they do not need. Dropping those silently would let "I selected 5" and "4 were
    granted" differ with nothing anywhere saying why, which is a worse outcome than the
    action itself — a bulk gate exists to make the scope of the click legible, and a
    scope quietly smaller than the selection defeats that as thoroughly as one quietly
    larger.

    A count rather than the rows themselves: the modal states how many were dropped and
    why, and the addresses that matter are the ones about to be EMAILED. Listing the
    excluded ones too would double the list and bury the recipients inside it.
  */
  const [bulkPricingGrantTarget, setBulkPricingGrantTarget] = useState<{
    rows: SignupEntry[]
    skipped: number
  } | null>(null)
  /*
    Allowlist gates. Both hold an ARRAY even for the single-row case, because both are
    reachable from the row kebab AND the bulk bar, and one modal serving both is what
    keeps their copy from drifting apart (§36). A one-element array renders the singular
    wording via `countPhrase`, so the shared modal costs the single-row case nothing.
  */
  const [sendLinkTarget, setSendLinkTarget] = useState<string[] | null>(null)
  const [removeAllowlistTarget, setRemoveAllowlistTarget] = useState<string[] | null>(null)

  /*
    VRG-247 (Waitlist pre-load, Batch 4b): comp / grant-activations pre-load —
    same actor gating + glyphs as the Company page (§21, §27).

    VRG-545 — these two actions now live on the ALLOWLIST tab's row kebab, not
    the Waitlist's. Founder decision, 2026-09-07: *"they need to be allowed in
    order to comp or grant makes the most sense — they get those both on sign
    up."* Being allowlisted is the PRECONDITION; the staged grant lands when
    they sign up.

    The old placement was actively misleading rather than merely early. Staging
    writes `pending_grants` on the signups row and touches NOTHING else — no
    allowlist entry, no invite (verified: `stage-pending-signup-grants.use-case.ts`
    contains zero references to the allowlist). So offering it beside "Allow"
    implied you could hand a comp to someone not yet allowed, for whom it would
    do nothing at all until somebody separately let them in.

    Targeted by EMAIL here because the allowlist table carries no signup id. The
    API resolves the signups row by address, creating a `granted` one when the
    email was added straight to the allowlist and never went through the
    waitlist — see StagePendingSignupGrantsUseCase.resolveEntry.
  */
  const { isHasAccess: canCompSubscription } = useCanAccess({ action: 'internal_company_change_subscription' })
  const { isHasAccess: canGrantActivations } = useCanAccess({ action: 'internal_company_add_cert_credits' })
  const compSubscriptionTooltip = !canCompSubscription
    ? 'You need the "Change subscription" permission. Ask an admin to grant it.'
    : null
  const grantActivationsTooltip = !canGrantActivations
    ? 'You need the "Add cert credits" permission. Ask an admin to grant it.'
    : null
  const [compTargetEmail, setCompTargetEmail] = useState<string | null>(null)
  const [grantTargetEmail, setGrantTargetEmail] = useState<string | null>(null)

  // ── Allowlist ───────────────────────────────────────────────────────────────
  const { entries, isLoading: isAllowlistLoading, addAsync: addAllowlistAsync, isAdding, remove: removeAllowlist } =
    useSignupAllowlist()
  /*
    The add control is a MODAL now, not a bare email field parked in the tab row.

    Founder directive, 2026-09-22. The old control could do exactly one thing —
    one address, typed by hand — while most of the addresses worth allowing are
    already on this page, one tab to the left. Allowing them meant reading an
    address off the Waitlist tab and retyping it here.

    `AddToAllowlistModal` follows the transfer-to-owner flow's shape rather than
    inventing one: pick from what exists, or name something that does not. See
    that component's docblock for which half came from where.
  */
  const [isAddAllowlistOpen, { open: openAddAllowlist, close: closeAddAllowlist }] =
    useDisclosure(false)
  /** What the modal compares against to refuse a duplicate. Derived, never a second copy. */
  const allowlistEmails = useMemo(() => entries.map((entry) => entry.email), [entries])

  /*
    VRG-545 — sending an allowlisted person their signup link.

    ⚠️ THE SAME PATH THE WAITLIST'S "Allow & invite" USES, not a new one:
    `POST /internal/signups/grant-email` → `GrantSignupUseCase` with `invite: true`. That
    use-case allowlists the address (idempotent — step 1 of its own docblock) and then, in
    the default `link` mode, emails the self-serve signup link. So running it against an
    address already on the allowlist re-sends the link and changes nothing else, which is
    exactly the action this tab was missing.

    Targeted by EMAIL because the allowlist table carries no signup id — same constraint the
    comp / grant-activations actions below already work under. The use-case resolves or
    creates the signups row behind the address itself.

    ⚠️ The surface this changes is the WAITLIST tab, not the Allowlist row you acted on:
    granting can CREATE a signups row for an address that only ever existed on the
    allowlist. The Allowlist row itself is unaffected — it was already allowed.

    ⚠️ It used to call `refetchSignups()` here, and that call was a PATCH over a broken
    mechanism rather than a considered belt-and-braces. `useGrantSignupByEmail` already
    invalidates `SIGNUPS_QUERY_KEY`, but the hook spread the caller's options AFTER its own
    `onSuccess`, so this toast silently replaced the invalidation — and the manual refetch
    is what made the loss invisible. The hook is fixed (see its docblock); the refetch is
    removed rather than kept, because a redundant one hides the next regression exactly as
    it hid this one.
  */
  const { mutate: inviteByEmail, isPending: isInviting } = useGrantSignupByEmail({
    onSuccess: () => notification({ type: 'success', title: 'Signup link sent' }),
    onError: (error: any) =>
      notification({ type: 'error', title: error?.message || 'Failed to send signup link' }),
  })

  /*
    N adds, one per address, sequentially.

    There is no batch route, and inventing one is out of this change's scope — the
    same call the page already makes, made N times, is what every other bulk action
    on this page does (`getAllowlistBulkActions`, `getSignupsBulkActions`).

    SEQUENTIAL rather than `Promise.all`, deliberately: the endpoint is a write per
    address and firing twenty at once buys nothing an operator can perceive while
    making a partial failure much harder to describe. `for await` also means the
    first failure stops the run, so the toast below can name a real count of what
    landed instead of a number that assumes the rest succeeded.

    ⚠️ The modal closes only on success. A failure leaves it open with the
    selection intact, because throwing away the operator's picks is a worse outcome
    than making them press Add again.
  */
  const handleAddAllowlist = async (emails: string[]) => {
    let added = 0
    try {
      for (const email of emails) {
        await addAllowlistAsync(email)
        added++
      }
    } catch (e: any) {
      notification({
        type: 'error',
        title: e?.message || 'Failed to add to allowlist',
        text: added > 0 ? `${added} of ${emails.length} were added before this failed.` : undefined,
      })
      return
    }

    notification({
      type: 'success',
      title: added === 1 ? 'Added to allowlist' : `${added} added to allowlist`,
    })
    closeAddAllowlist()
  }

  const signupsColumns = useMemo(
    () => [
      {
        field: 'user',
        headerName: 'User',
        type: 'string',
        width: 250,
        sortable: false,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Box sx={{ ...cellFlexSx, flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
            <Typography variant="body3" noWrap>
              {row.name ?? ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.email}
            </Typography>
          </Box>
        ),
        /*
          VRG-595 — `field: 'user'` is not a key on the row, so `row['user']` is undefined
          and this column exported an EMPTY cell. Silent, and exactly the kind of gap the
          export is least likely to be checked for: the CSV had a "User" header with
          nothing under it. `csvValue` is the explicit opt-in that always wins
          (`toCsvRows`), so the export now carries what the cell shows.
        */
        csvValue: (row: SignupEntry) => row.name ?? '',
      },
      /*
        VRG-595 — FIRST and LAST as their own columns.

        ⚠️ A BLANK HERE IS NOT "the visitor left it blank". vera-website asked for one
        "Full name" until 2026-09-08; every row from before that has a `name` and nulls
        here, and nothing backfills them. Backfilling would mean splitting `name` on a
        space, which is wrong precisely where it matters — "van der Berg" is a two-word
        surname, "Ana María García Pérez" is two of each, a mononym has no last name —
        and a fabricated surname is indistinguishable from a real one. So the collector
        was changed and the past was left honest. `User` still shows `name` for every row.

        They earn columns rather than living inside the User cell because the thing a
        reviewer does with a first name is address someone by it, usually in bulk — and a
        column is what the CSV export can carry. `field` matches the row key, so the
        export picks them up with no `csvValue`.
      */
      {
        field: 'first_name',
        headerName: 'First name',
        type: 'string',
        // §49 — sized for the HEADER plus its info glyph, not for the values.
        width: 160,
        disableColumnMenu: true,
        renderHeader: headerWithInfo(
          'First name',
          'Collected separately by the marketing site since 8 September 2026. Blank on earlier rows, which recorded one full name. Never split from it.',
        ),
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Typography variant="body3" noWrap sx={cellFlexSx}>
            {row.first_name ?? ''}
          </Typography>
        ),
      },
      {
        field: 'last_name',
        headerName: 'Last name',
        type: 'string',
        width: 160,
        disableColumnMenu: true,
        renderHeader: headerWithInfo(
          'Last name',
          'Collected separately by the marketing site since 8 September 2026, and optional there. Blank means either an earlier row or a visitor who gave one name.',
        ),
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Typography variant="body3" noWrap sx={cellFlexSx}>
            {row.last_name ?? ''}
          </Typography>
        ),
      },
      {
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 220,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupEntry }) => {
          const url = row.company_website ?? null
          return (
            <Stack direction="row" spacing={1} alignItems="center" sx={cellFlexSx}>
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {row.company_name ?? ''}
              </Typography>
              {url && (
                <Tooltip title={`Open ${url}`}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); openExternalLink(url) }}
                    sx={{ color: 'text.secondary', borderRadius: 1.5, flexShrink: 0, '&:hover': { color: 'text.primary' } }}
                  >
                    <ArrowUpRightIcon size={14} strokeWidth={1.5} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'apollo',
        headerName: 'Apollo',
        type: 'custom',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupEntry }) => {
          const url = buildExternalLink(EXTERNAL_LINK_BASES.apollo, row.apollo_org_id)
          return (
            <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
              {url ? (
                <Tooltip title="Open in Apollo">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); openExternalLink(url) }}
                    sx={{ color: 'text.secondary', borderRadius: 1.5, '&:hover': { color: 'text.primary' } }}
                  >
                    {BRAND_ICONS['apollo']}
                  </IconButton>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.disabled"></Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        type: 'string',
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary" noWrap>
              {row.source ?? ''}
            </Typography>
          </Box>
        ),
      },
      // VRG-728 — where the visit that submitted came from (UTMs, else referrer, else landing page).
      buildFirstTouchColumn(),
      /*
        VRG-658 — "Referred by": a brand (the referring company, and the member who sent it),
        an Insider (the FirstPromoter ref), or empty. The API sorts referred PENDING rows to
        the top of the queue; none of them skip it, so this column is how a reviewer sees
        why a row is up there.
      */
      {
        field: 'referral',
        headerName: 'Referred by',
        type: 'string',
        width: 220,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_: unknown, row: SignupEntry) => referredByOf(row.referral)?.primary ?? '',
        renderCell: ({ row }: { row: SignupEntry }) => <ReferredByBlock referral={row.referral} />,
      },
      // VRG-545 — still DERIVED from the `roi_submission` blob, never a second stored
      // value that could disagree with the blob it describes. What changed is that it
      // now counts HOW MUCH of the calculator the visitor filled in ("6/10") rather
      // than restating that they submitted at all, which was true of every row that
      // had the blob and therefore separated nothing. See `roiInputsColumn.tsx` for
      // the three states and for why an uncountable submission keeps the old words.
      buildRoiInputsColumn(),
      {
        field: 'created_at',
        // VRG-545: was "Signed up", which describes something that has not
        // happened. This is a WAITLIST — the row records the moment somebody
        // asked for access, and most rows never become a signup at all. The old
        // label read as "this person has an account", which is the opposite of
        // what a reviewer is here to decide.
        headerName: 'Requested access',
        type: 'dateTime',
        width: 175,
        // Sortable, unlike before: "who asked first" is the ordering a reviewer
        // actually works this queue in.
        sortable: true,
        /*
          ⚠️ MUI X v7 signature `(value, row, …)` — this was `({ value }) => …`, the v6
          shape, and under v7 the first argument is the VALUE ITSELF (a string), not a
          params object. Destructuring `value` off a string primitive yields `undefined`,
          so this returned `null` for every row and the column that was made sortable
          sorted nothing — silently, because a stable order looks exactly like a sorted
          one on a short list. Found while adding the website columns below, which is the
          same failure this whole ticket is about: a control that reports success and
          does nothing.

          The typecheck named it too (`TS7031: Binding element 'value' implicitly has an
          'any' type`) and it sat inside the platform's 955-error baseline, where a real
          defect is indistinguishable from noise.
        */
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {row.created_at ? date(row.created_at) : ''}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        /*
          VRG-Pricing.Access.Gate — was "Status", which stopped identifying anything
          the moment a SECOND status column landed beside it. "Status" and "Pricing
          access" side by side invite the reading that one is the state and the other
          a detail of it, when they answer two independent questions: may this person
          create a Vera account, and may they read the price list. Naming the first
          one after its own question is what makes the pair legible.

          The VALUES are unchanged (Pending / Allowed / Rejected, `SIGNUP_STATUSES`) —
          this renames the header only, so nothing downstream of `field: 'status'`
          moves, including the CSV export's own column key.
        */
        headerName: 'Signup allowed',
        type: 'string',
        // §49 — sized for the HEADER, not the values. "Signup allowed" is ~96px at
        // 14px plus the grid's own cell padding; the old 130 was cut for "Status"
        // (~42px) and truncates the longer label. No sort or menu glyph to budget
        // for here (`sortable: false`, `disableColumnMenu`), so 150 is the label
        // plus margin rather than the label plus chrome.
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupEntry }) => (
          <Box sx={cellFlexSx}>
            <StatusChip size="small" statuses={SIGNUP_STATUSES} status={row.status} />
          </Box>
        ),
      },
      /*
        VRG-Pricing.Access.Gate — is this person admitted to the gated /pricing
        page on the marketing site?

        A COLUMN, not just a kebab row, because hiding the inapplicable action
        (§46) means the menu no longer tells you which state a row is in — you
        would have to open it to find out, one row at a time. The column is what
        makes the hidden-action treatment legible across the list.

        DERIVED from the two timestamps, never a third boolean field that could
        disagree with the data it describes — the same argument
        `roi_submission != null` makes one column over.

        Three states rather than two: "Revoked" is deliberately distinct from
        "No", because "we took this away" and "we never gave it" are different
        facts about a lead and the difference matters when someone asks why a
        link stopped working.

        ⚠️ A `StatusChip`, and this REVERSES an earlier note that sat here arguing
        for plain text because "these are not `SIGNUP_STATUSES` values and
        inventing a fourth status map for one column would put two vocabularies on
        one row". Two things were wrong with it. The vocabularies were already
        two — `status` and `pricing_access` answer different questions about the
        row and always did — so the map count was never what made them one or
        two. And the cost it avoided is smaller than the one it created: this
        column sits DIRECTLY BESIDE Status, which renders pills, so every row read
        as a pill next to a bare word.

        `SIGNUP_PRICING_ACCESS_STATUSES` is the map and `StatusChip` is the same
        component Status uses one column over, not a second chip (§28).

        The em-dash SURVIVES the change. Never-granted is a genuine empty state,
        not a third status, and a pill on every row nobody has considered yet is
        how a column of chips stops carrying information.
      */
      {
        field: 'pricing_access',
        headerName: 'Pricing access',
        type: 'string',
        // Sized for the HEADER, not the values (§49): "Pricing access" plus the
        // sort glyph needs far more than "Revoked" does.
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupEntry }) => {
          const pricingAccessStatus = signupImpliesPricingAccess(row)
            ? 'granted'
            : row.pricing_access_revoked_at
              ? 'revoked'
              : null
          // Only when the GRANT comes from signup access rather than from a
          // pricing grant of its own is there something extra to explain.
          const impliedNote = row.status === 'granted' ? impliedPricingAccessNote(row) : null
          const chip = pricingAccessStatus ? (
            <StatusChip
              size="small"
              statuses={SIGNUP_PRICING_ACCESS_STATUSES}
              status={pricingAccessStatus}
            />
          ) : (
            <Typography variant="body3" color="text.disabled">
              {EMPTY_VALUE}
            </Typography>
          )
          return (
            <Box sx={cellFlexSx}>
              {impliedNote ? (
                /*
                  A `span`, not the chip, and `cursor: help` rather than `pointer` —
                  both for the reasons `StatusChip` already records for its own
                  glossary tooltip: MUI's Tooltip needs a child that holds a ref, and
                  a pointer cursor inside a table row promises a destination this
                  does not have.

                  Wrapped HERE rather than adding a `tooltip` prop to `StatusChip`:
                  the shared component already carries one tooltip channel
                  (`glossaryId`, a DEFINITION of the status word), and this says
                  something about THIS ROW, not about the word "Granted". A second,
                  free-text channel on a component rendered in ~40 places is how a
                  status chip ends up explaining a definition on one surface and a
                  record's history on another. §36's trigger is the second hand-roll;
                  this is the first.
                */
                <Tooltip title={impliedNote}>
                  <span style={{ display: 'inline-flex', width: 'fit-content', cursor: 'help' }}>
                    {chip}
                  </span>
                </Tooltip>
              ) : (
                chip
              )}
            </Box>
          )
        },
      },
      /*
        VRG-545 — the marketing site's own fields, one column each, generated from
        `WEBSITE_FIELDS`.

        Placed AFTER the queue columns and before the pinned actions, deliberately. A
        reviewer works this list by who asked and what state they are in; the submission
        figures are what they reach for once a row is worth looking at. Threading fifteen
        columns in ahead of Status would push the queue's own answer off the first screen
        to make room for a lead's guess at its counterfeit rate.

        The table scrolls horizontally at this width — accepted. Every one of these is in
        the toolbar's Layout panel, so a reviewer who wants a narrower table switches off
        the ones they are not comparing on.
      */
      /*
        VRG-545 — the qualification headlines, placed FIRST in this block rather than after
        the fifteen submission columns they are derived from. A reviewer scanning for "which
        of these is worth a call" is reading these two; the figures behind them are what they
        open once a row is worth looking at. Putting them last would bury the summary behind
        its own working.

        The two sit side by side because they are the two halves of what a lead is worth to
        Vera — what they would pay us to run the programme, and what we would earn on the
        resales it enables. They are NOT summable:

          · different CURRENCIES (subscription is Vera's USD price list; brokerage is the
            visitor's own, from their `price`), and nothing here converts;
          · different GATES, so one can be present while the other is blank on the same row.
            Subscription needs `units` alone; brokerage needs units, price, resaleRate and
            resaleValueRatio all to be the visitor's. A blank beside a figure means "we
            cannot honestly price this half", never "zero".

        Both tooltips state their own currency for that reason. Deliberately no total column.
      */
      buildPotentialRevenueColumn(plans),
      buildBrokerageRevenueColumn(),
      ...buildWebsiteColumns(),
      ...buildConsentColumns((row: SignupEntry) => row.waitlist_submission),
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        renderCell: ({ row }: { row: SignupEntry }) => {
          // granted → can only Remove signup access. Not-yet-granted → Allow / Allow & invite,
          // then Reject if pending.
          //
          // VRG-545: Comp subscription / Grant activations are NOT here any more — they
          // moved to the Allowlist tab, because being allowed is the precondition for
          // either. See the note beside `compTargetEmail` above.
          /*
            VRG-Pricing.Access.Gate — pricing access is RECORD STATE, so exactly
            one of the two rows is shown and the other is HIDDEN, never disabled
            (§46: permission disables, record state hides). "Revoke" on someone
            who has nothing to revoke is a tombstone, not information.

            Hiding creates an obligation and it is met here: the action that
            RESOLVES each state is the one on offer. Not-granted shows Grant;
            granted shows Revoke; a revoked row falls back to Grant, which
            re-grants and clears the revocation.

            `XIcon`, not a trash can (§38): revoking is set-removal, not record
            deletion — the person, the row and the audit trail all survive. And
            it carries no `destructive` flag for the same reason (§32/§73): it is
            reversible by construction, one click in the other direction.

            `DollarSignIcon` on Grant, because the thing being granted is access
            to a PRICE LIST. It was `TagIcon`, which named nothing on this row:
            a tag is the physical product, and the only reading of it here was
            "something to do with tags", i.e. the wrong feature entirely.

            ⚠️ GRANT OPENS A GATE; REVOKE DOES NOT. Granting emails a single-use
            magic link into Vera's real pricing, which is a message to a prospect
            and cannot be recalled, so it earns a modal that names that (§43).
            Revoking sends nothing and is one click from being undone, so gating
            it would be the modal fatigue §43 warns about spending the budget on.
          */
          /*
            ⚠️ AN ALLOWED ROW GETS NEITHER PRICING ACTION — see
            `signupImpliesPricingAccess` for the implication and for the ruling on a
            row that also carries a stored revocation.

            The founder's words named only the Grant half: *"if the user is already
            allowed to sign up, then they should already have access to the pricing
            page so Grant pricing access should no longer show up."* Taken literally
            that leaves Revoke standing, and Revoke on an allowed person is the worse
            of the two rows to keep — it reads as taking something away and does not.
            `RevokePricingAccessUseCase` stamps the column and kills outstanding
            tokens, and its own docblock then says the handoff route "does not consult
            the signup row at all", so the person signs in and reads the price list
            regardless. An action that reports success and changes nothing observable
            is exactly the control this whole ticket keeps finding and removing.

            So the implication removes BOTH. What remains on the menu is the action
            that genuinely resolves "I do not want this person seeing our prices":
            Remove signup access, one row below. That is §46's obligation met —
            hiding is only correct while the resolving action is reachable.

            RECORD STATE, so the item is simply absent rather than `disabled` (§46).
            There is no tooltip to write here: "this is unavailable because they are
            allowed" is not a blocker the operator resolves, it is a fact the Signup
            allowed column already states one cell to the left.

            ⚠️ The test is `row.status`, NOT `signupImpliesPricingAccess`. A row that is
            NOT allowed but holds a live pricing grant is the smaller grant working
            exactly as designed — a prospect shown the price list without being
            admitted to the platform — and Revoke is the real, effective action there
            (no platform session means no handoff route to fall back on). Folding that
            case in would have deleted the one place revocation still does something.
          */
          const pricingAccessItem =
            row.status === 'granted'
              ? null
              : pricingAccessIsLive(row)
                ? {
                    label: 'Revoke pricing access',
                    icon: <XIcon strokeWidth={1} />,
                    onClick: () => revokePricingAccess({ signupId: row.id }),
                  }
                : {
                    label: 'Grant pricing access',
                    icon: <DollarSignIcon strokeWidth={1} />,
                    onClick: () => setPricingGrantTarget(row),
                  }

          /*
            ORDER: pricing first, then the allow actions, destructive last.

            Founder directive, 2026-09-21 — *"'Allow' and 'Allow and invite'
            should be after those on pricing."* The reasoning is escalating
            commitment, which is the same axis §73 orders Archive and Delete on:
            letting somebody read a price list is a smaller thing than letting
            them onto the platform, and letting them onto the platform is smaller
            than refusing them. A menu read top to bottom now costs more with
            every row.

            Reject / Remove signup access stay last behind a divider (§73) — unchanged.
          */
          const items =
            row.status === 'granted'
              ? [
                  /*
                    No pricing row at all here — see `pricingAccessItem` above. That
                    leaves this branch with ONE item, which is correct rather than
                    thin: an allowed person has already been through every decision
                    this menu offers, and the only thing left to do to them is undo it.

                    "Remove signup access", not "Remove access". Founder directive,
                    2026-09-22, and the reason is this page's own doing: it now shows
                    two kinds of access on one row, so a bare "access" names neither.
                    The action is unchanged (`rejectSignup`), as is the glyph — §38
                    calls this set-removal rather than record deletion (the person, the
                    row and the audit trail all survive), which is why it is a
                    `CircleXIcon` and not a trash can.
                  */
                  {
                    label: 'Remove signup access',
                    icon: <CircleXIcon strokeWidth={1} />,
                    onClick: () => rejectSignup({ signupId: row.id }),
                    destructive: true,
                    divider: true,
                  },
                ]
              : [
                  // Deliberately reachable WITHOUT a platform grant, and now
                  // FIRST. Sending a prospect the price list is a sales
                  // conversation; requiring them to be allowlisted for an
                  // account first would invert the real order of events.
                  //
                  // Non-null on this branch by construction (`row.status !==
                  // 'granted'`), but narrowed rather than asserted so a future change
                  // to the condition above cannot silently render a hole in the menu.
                  ...(pricingAccessItem ? [pricingAccessItem] : []),
                  {
                    label: 'Allow',
                    icon: <CircleCheckIcon strokeWidth={1} />,
                    onClick: () => setAllowTarget(row),
                  },
                  {
                    label: 'Allow & invite',
                    icon: <MailIcon strokeWidth={1} />,
                    onClick: () => setInviteTarget(row),
                  },
                  ...(row.status === 'pending'
                    ? [
                        {
                          label: 'Reject',
                          icon: <CircleXIcon strokeWidth={1} />,
                          onClick: () => rejectSignup({ signupId: row.id }),
                          destructive: true,
                          divider: true,
                        },
                      ]
                    : []),
                ]
          return <ActionDropdown items={items} />
        },
      },
    ],
    [rejectSignup, revokePricingAccess, plans]
  )

  /*
    Both items OPEN A GATE; neither mutates. They used to call `grantSignup` for every
    selected row on click, so the bulk bar performed — with no confirmation — the same
    two actions the row kebab gates individually, and "Allow & invite" put N
    unrecallable emails one click away. The single-row gates made that asymmetry worse
    rather than better: the cheaper action asked, the costlier one did not.

    The fan-out itself is unchanged and still lives in the gate's `onConfirm` (N
    mutations, no batch route — see `getAllowlistBulkActions` for why inventing one is
    out of scope).
  */
  const getSignupsBulkActions = useCallback(
    (selectedRows: SignupEntry[]) => {
      /*
        VRG-Pricing.Access.Gate — "Grant pricing access" was row-only, which made the
        cheapest, most repetitive thing on this page the one action you could not do
        in bulk: mailing a price list to the twelve leads from one campaign meant
        twelve kebabs and twelve modals.

        The ELIGIBLE set is not the selection. An allowed person already has pricing
        access, so granting again would stamp columns that are already stamped and
        mail a 7-day magic link they have no use for. Same rule as the row kebab,
        which hides the action entirely on those rows — the bulk bar cannot hide it
        per row, so it narrows the set and the gate says so.
      */
      const pricingEligible = selectedRows.filter((row) => row.status !== 'granted')

      return [
        {
          label: 'Grant pricing access',
          icon: <DollarSignIcon strokeWidth={1} />,
          /*
            HIDDEN when nothing in the selection is eligible (§46: record state hides).
            Left visible it would be a control that does nothing on click, which is
            strictly worse than absence — and there is no tooltip to write, because
            the operator resolves it by changing their selection, not by asking anyone
            for anything. `Table` filters on `visible !== false`.
          */
          visible: pricingEligible.length > 0,
          onClick: () =>
            openBulkGate(pricingEligible, (rows) =>
              setBulkPricingGrantTarget({ rows, skipped: selectedRows.length - rows.length })
            ),
        },
        /*
          Allow and Allow & invite keep the WHOLE selection, deliberately. Re-allowing
          an already-allowed row is idempotent at the API (`GrantSignupUseCase`'s
          allowlist add is idempotent by its own docblock) and "Allow & invite" on one
          re-sends the signup link, which is a real thing an operator may want. Only
          the pricing grant narrows, because only it is made meaningless by the
          implication.

          Order matches the row kebab: pricing first, then the two allow actions. Same
          escalating-commitment reasoning recorded there.
        */
        {
          label: 'Allow',
          icon: <CircleCheckIcon strokeWidth={1} />,
          onClick: () => openBulkGate(selectedRows, setBulkAllowTarget),
        },
        {
          label: 'Allow & invite',
          icon: <MailIcon strokeWidth={1} />,
          onClick: () => openBulkGate(selectedRows, setBulkInviteTarget),
        },
      ]
    },
    []
  )

  /*
    VRG-545 — ONE definition of what you can do to an allowlisted address, read by the row
    kebab and by the bulk-actions bar (§36: the second hand-roll is the trigger to share, and
    two lists of the same actions is precisely how a bulk bar comes to offer something the
    row does not, or to gate it differently).

    `bulkSafe` is the axis the two surfaces differ on, and it is a property of the ACTION
    rather than of the surface: an action that fans out over N addresses is bulk-safe; one
    that opens a modal about ONE address is not. Comp subscription and Grant activations are
    the second kind — `CompSubscriptionModal` takes a single `email` and stages one grant, so
    a bulk entry for it could only ever act on one of the selected rows while appearing to
    act on all of them. Offering it and quietly doing less is worse than not offering it.
  */
  const allowlistActions = useCallback(
    (email: string): AllowlistAction[] => [
      {
        label: 'Send signup link',
        icon: <MailIcon strokeWidth={1} />,
        // GATED. Same `signup-invitation` email as the Waitlist's "Allow & invite", to one
        // person, unrecallable — so it earns the same confirm rather than firing on the
        // click of a kebab row. It had none, which made this the one place on the page
        // where a message left the building with nothing asked.
        onClick: () => setSendLinkTarget([email]),
        bulkOnClick: (emails) => openBulkGate(emails, setSendLinkTarget),
        // §46: an in-flight mutation DISABLES with a reason, never hides — an item that
        // vanishes under the cursor mid-click is the failure that rule names.
        disabled: isInviting,
        tooltip: isInviting ? 'Sending…' : undefined,
        bulkSafe: true,
      },
      {
        label: 'Comp subscription',
        icon: <GiftIcon strokeWidth={1} />,
        onClick: () => setCompTargetEmail(email),
        disabled: !!compSubscriptionTooltip,
        tooltip: compSubscriptionTooltip ?? undefined,
        bulkSafe: false,
      },
      {
        label: 'Grant activations',
        icon: <CoinsIcon strokeWidth={1} />,
        onClick: () => setGrantTargetEmail(email),
        disabled: !!grantActivationsTooltip,
        tooltip: grantActivationsTooltip ?? undefined,
        bulkSafe: false,
      },
      {
        label: 'Remove',
        icon: <Trash2Icon strokeWidth={1} />,
        /*
          GATED — and this one was found while gating the two above rather than asked for,
          so it is called out here for review rather than folded in silently.

          §32 is unambiguous that a destructive action has a confirmation modal, and this
          had none on either surface: a row's kebab and the bulk bar both deleted
          allowlist entries on a single click. That is the same asymmetry the other gates
          close, one step worse, because removing an address revokes someone's ability to
          create an account and nothing on the page records that it happened.

          §73/§38 ordering already held and is untouched: Remove is last, below a divider,
          `destructive: true`, and a trash glyph is correct because the row genuinely
          ceases to exist rather than being unselected.
        */
        onClick: () => setRemoveAllowlistTarget([email]),
        bulkOnClick: (emails) => openBulkGate(emails, setRemoveAllowlistTarget),
        destructive: true,
        divider: true,
        bulkSafe: true,
      },
    ],
    [isInviting, compSubscriptionTooltip, grantActivationsTooltip]
  )

  /*
    The bulk bar, driven by the same list. `Table`'s `bulkActions` takes
    `(selectedRows) => items[]` and is the established pattern on this table already — the
    Waitlist tab above uses it, as do ~10 other list pages (§28: reuse before hand-rolling).

    Each item re-derives its own handler per selected row rather than calling a batch
    endpoint, matching `getSignupsBulkActions`'s shape above. There is no batch route for
    either action, and inventing one here would be scope this ticket has no verification
    for; N mutations against a page-sized selection is what the Waitlist already does.
  */
  const getAllowlistBulkActions = useCallback(
    (selectedRows: SignupAllowlistEntry[]) =>
      // The empty address builds the SHAPE only — labels, icons, gating, order. Every
      // handler is re-derived per row by INDEX into the same factory, so the bulk item and
      // the kebab item at that position are the same action by construction rather than by
      // a label lookup that a rename would silently break.
      allowlistActions('').flatMap((template, index) => {
        if (!template.bulkSafe) return []
        const { bulkSafe: _bulkSafe, bulkOnClick, ...item } = template
        const emails = selectedRows.map((row) => row.email)
        return [
          {
            ...item,
            /*
              `bulkOnClick` wins when the action declares one, and every GATED action must
              — repeating a modal-opening `onClick` per row would collapse N `setState`
              calls into one and confirm a single address while appearing to confirm all
              of them. See `AllowlistAction.bulkOnClick`.

              The per-row fan-out below remains for actions that still mutate directly on
              click; it is the original behaviour, not a fallback nobody reaches.
            */
            onClick: bulkOnClick
              ? () => bulkOnClick(emails)
              : () => selectedRows.forEach((row) => allowlistActions(row.email)[index].onClick()),
          },
        ]
      }),
    [allowlistActions]
  )

  const allowlistColumns = useMemo(
    () => [
      {
        field: 'email',
        headerName: 'Email',
        type: 'string',
        /*
          VRG-545 — 300 is `Search`'s own width (its `minWidth: 300`), so this pinned
          first column and the toolbar search box directly above it share BOTH edges.
          Their left edges already lined up; the right ones did not.

          ⚠️ `flex: 0` IS THE LOAD-BEARING HALF, not `width`. `Table` runs
          `withProportionalFlex`, which treats an authored `width` as a FLOOR and then
          flexes every column to fill the viewport — so `width: 300` alone rendered at
          445px on a 1280 desktop (measured), 145px past the field it is supposed to
          match. That helper bails out entirely the moment ANY column declares `flex`,
          which is why the columns below carry explicit flex values: opting this one
          column out means authoring the whole table's distribution, and the
          alternative (leaving them all fixed) parks ~600px of dead space to the right.

          `flex: 0` is non-null (so the helper stands down) and falsy (so MUI uses
          `width`) — both properties are required and neither is incidental.

          A literal rather than an import because `Search` does not export the number.
          If that field is ever resized, this has to move with it.
        */
        width: 300,
        flex: 0,
        sortable: false,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" noWrap>{row.email}</Typography>
          </Box>
        ),
      },
      /*
        VRG-545 — the four columns that answer "did letting this person in lead
        anywhere", which nothing on this page could answer before. Allowing an email
        was the end of the record; whether they ever became a customer lived only in
        the companies/subscriptions tables, several clicks away and keyed on nothing
        this page displayed.

        Order is the funnel, left to right: what state are they in → who did they
        become → what did they buy → when. All four resolve server-side in ONE
        batched join (see `listWithConversion`), never a lookup per row.

        EVERY column below carries `flex` AND `minWidth`, and both are consequences
        of the Email column's `flex: 0` above — read that note first. `flex` mirrors
        the authored width so slack is shared in the same proportion
        `withProportionalFlex` would have used; `minWidth` is the §49 floor, so a
        narrower viewport shrinks these columns only down to the width their HEADER
        needs and no further.
      */
      {
        field: 'conversion_status',
        headerName: 'Status',
        type: 'string',
        // §49: "Status" is ~42px of text at 14px, plus the grid's ~63px of header
        // chrome (sort-icon container + title padding + cell padding) ≈ 105 — the
        // header's floor. The cell is what sets this width now: VRG-639 puts a
        // second chip ("Tracked") beside "Converted", and the pair needs ~200.
        width: 200,
        minWidth: 200,
        flex: 200,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <Box sx={{ ...cellFlexSx, gap: 1 }}>
            <StatusChip
              size="small"
              statuses={SIGNUP_CONVERSION_STATUSES}
              status={row.conversion_status}
            />
            {/*
              VRG-639 — "Tracked": this company's became_client event reached the
              website's PostHog, so the marketing funnel can see this conversion.
              Absent (not a negative chip) for everything else — see
              SIGNUP_TRACKING_STATUSES for why the skipped states stay invisible.
              The hover text is the glossary's `became-client-tracked` entry (§44/§59),
              not a sentence typed here.
            */}
            {row.company?.tracking_status === 'sent' && (
              <InfoTooltip term="became-client-tracked">
                <span style={{ display: 'inline-flex', width: 'fit-content', cursor: 'help' }}>
                  <StatusChip size="small" statuses={SIGNUP_TRACKING_STATUSES} status="sent" />
                </span>
              </InfoTooltip>
            )}
          </Box>
        ),
      },
      {
        field: 'company',
        headerName: 'Company',
        type: 'string',
        // Sized for the CELL here, not the header, and that is not a §49 exception:
        // §49's rule is that a header must never be the thing that truncates. The
        // header ("Company", ~125px with chrome) fits inside this comfortably —
        // what needs the room is `CompanyCell`'s 46px logo plus the brand name.
        width: 240,
        minWidth: 240,
        flex: 240,
        sortable: false,
        disableColumnMenu: true,
        // Feeds CSV export + the column menu, which never call `renderCell`.
        valueGetter: (_value: unknown, row: SignupAllowlistEntry) => row?.company?.name ?? '',
        renderCell: ({ row }: { row: SignupAllowlistEntry }) =>
          row.company ? (
            // §36/§28 — the shared reference cell, not a fourth hand-rolled company
            // preview. It already carries the logo→initials fallback and the §42
            // referrer, so the company page it lands on can offer "← Back to Signups".
            <CompanyCell
              company={{
                id: row.company.id,
                subdomain: row.company.subdomain,
                name: row.company.name,
                logo: row.company.logo ?? '',
              }}
            />
          ) : (
            <Box sx={cellFlexSx}>
              <Typography variant="body2" color="text.disabled">
                {EMPTY_VALUE}
              </Typography>
            </Box>
          ),
      },
      {
        field: 'subscription',
        headerName: 'Subscription',
        type: 'string',
        // "Subscription" is ~84px + ~63px chrome ≈ 147. 200 leaves room for the
        // cell's own two lines (plan name over "Started <date>") without either
        // wrapping, so the header cannot be the thing that clips.
        width: 200,
        minWidth: 200,
        flex: 200,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_value: unknown, row: SignupAllowlistEntry) =>
          row?.subscription?.plan_title ?? '',
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <SubscriptionCell
            subscription={row.subscription}
            // Subdomain preferred over id, matching CompanyCell's own route argument.
            companyRef={row.company?.subdomain ?? row.company?.id ?? null}
          />
        ),
      },
      {
        field: 'signed_up_at',
        headerName: 'Signed up',
        type: 'dateTime',
        // ⚠️ NOT the same date as "Added to allowlist" below, and the two labels are
        // spelled out rather than left as "Signed up" / "Added" precisely so nobody
        // reads them as the same fact. This one is `users.created_at` — when the
        // account was actually created. The other is when staff let the email
        // through the gate, which for most rows is a date no signup ever followed.
        width: 170,
        minWidth: 170,
        flex: 170,
        // Sortable: "who actually converted, most recently" is a real ordering to
        // work this list in. Nulls sink, which is the useful end for them.
        sortable: true,
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <Box sx={cellFlexSx}>
            <Typography
              variant="body2"
              color={row.signed_up_at ? 'text.secondary' : 'text.disabled'}
            >
              {row.signed_up_at ? date(row.signed_up_at) : EMPTY_VALUE}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'created_at',
        // VRG-545: was "Added", which does not say added to WHAT — and now sits
        // beside a second date on the same row. See the note on `signed_up_at`.
        headerName: 'Added to allowlist',
        type: 'dateTime',
        // §49, and this one is genuinely header-bound: "Added to allowlist" is
        // ~124px at 14px plus ~63px of grid header chrome ≈ 187. 200 leaves ~13px,
        // enough that a font-metric estimate being slightly off cannot produce the
        // clip this rule exists to prevent. The VALUES ("Aug 28 2026") need barely
        // half of it.
        width: 200,
        minWidth: 200,
        flex: 200,
        sortable: false,
        // Same v6→v7 signature fix as the Waitlist tab's date column above. Harmless here
        // today (this column is not sortable), but a `valueGetter` that always returns
        // null still feeds the CSV export, and leaving one of the pair wrong is how the
        // next person concludes the shape is fine.
        valueGetter: (value: unknown) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {row.created_at ? date(row.created_at) : ''}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        /*
          VRG-545 — the items come from `allowlistActions`, shared with the bulk
          bar so the two cannot offer different things (§36).

          "Send signup link" leads because it is the action this tab was MISSING:
          the Waitlist can "Allow & invite", but the Allowlist — where somebody is
          already permitted — had no way to actually send them the link, so an
          address allowed weeks ago sat there with nothing having been sent.

          Comp subscription and Grant activations landed here from the Waitlist
          kebab. Both are PRE-LOADS: they stage the grant on this person's signups
          row and it applies the moment they become a company
          (`ApplyPendingSignupGrantsUseCase`). Nothing bills, and no Stripe or
          ledger call happens now.

          Order follows §73's shape — the non-destructive actions first, the
          destructive Remove last, behind a divider. Permission gating is §27/§46:
          VISIBLE and disabled with a tooltip naming the permission, never
          hidden, because the resolution is somebody else's to grant. Every row
          carries a leading icon (§21).
        */
        renderCell: ({ row }: { row: SignupAllowlistEntry }) => (
          <ActionDropdown
            items={allowlistActions(row.email).map(({ bulkSafe: _bulkSafe, ...item }) => item)}
          />
        ),
      },
    ],
    [allowlistActions]
  )

  // AFTER every hook, never before — an early return above them would change the hook
  // count between renders the moment `isHasAccess` resolves.
  if (!isAdmin) {
    return <Navigate to={ROUTES.INTERNAL} replace />
  }

  return (
    <>
      {/*
        The page title Labs used to supply. Its action group is deliberately EMPTY: the
        primary action here is per-TAB (Invite on Waitlist, an email field + Add on
        Allowlist), so it belongs beside the tabs that scope it. Hoisting it into the
        header would put the allowlist's email input on screen while the Waitlist tab is
        showing, which is a control that acts on something you are not looking at.
      */}
      {/* The page holds two different things under one title, and only one of them is a
          term of art: the Waitlist is a list of people who asked, the Allowlist is the
          GATE deciding who may complete signup at all. The definition follows the tab
          rather than sitting on the title permanently, so it appears where it means
          something. (Not on the tab label itself — those render inside a ButtonBase, so a
          trigger there would switch tabs as a side effect of asking what a word means.) */}
      {/* Titled as what it now is (Spencer, 2026-10-08; VRG-727 renamed the page Access): the
          sidebar's "Access" lands on /internal/access, and this is that page's classic view,
          kept for the actions Access does not carry. Never a second page called just "Access". */}
      <Header
        title={CLASSIC_ACCESS_TITLE}
        titleAdornment={tab === 'allowlist' ? <InfoTooltip term="allowlist" /> : undefined}
      />
      <Stack spacing={4}>
      {/* VRG-470: underline page-header tabs. The per-tab action moves from an outer
          space-between Stack onto the tab row's own `rightComponents`, so the rule runs the
          full width under both — the outer Stack would have left the action outside the band.
          The action stays BESIDE the tabs deliberately: it is per-TAB (Invite on Waitlist,
          Add to allowlist on Allowlist), and the note above records why hoisting it into the
          header would put an allowlist control on screen while Waitlist is showing. */}
      <Tabs
        redirect={false}
        tabs={TABS}
        value={tab}
        onChange={setTab}
        rightComponents={tab === 'invites' ? (
          <Button variant="primary" disableElevation onClick={openCreateInvite} startIcon={<KeyRoundIcon strokeWidth={1} />}>
            Create invite
          </Button>
        ) : tab === 'waitlist' ? (
          <Button variant="primary" disableElevation onClick={openGrantModal} startIcon={<UserPlusIcon strokeWidth={1} />}>
            Invite
          </Button>
        ) : (
          /*
            Allowlist add sits in the primary-action slot (top-right), same place as Invite,
            and is now the SAME SHAPE as it — one button, default size, same glyph.

            ## Why `size="large"` is gone, with the numbers

            It was 49px on purpose, and the reason was sound while it lasted: §29's bands are
            per-BAND, and a control sharing a row with a 49px FIELD matches the field. The
            field is what justified it.

            The field has moved into the modal, so nothing in this row is 49 any more — and a
            lone 49px button beside a 40px one on the neighbouring tab is the band disagreeing
            with itself. Measured at 1280x800 before the change: Invite sat 39px above the
            content below it, Add sat 35px. Both controls are centred in the same 53px tab row,
            so the 9px height difference put their bottom edges 4px apart, and the gap under
            each inherited that. At the default 40 the two are identical by construction
            rather than by a hand-set margin — which is the only kind of equality that
            survives someone editing one of them (§29: the gap is a pixel target, and here the
            way to hit it is to stop making the two rows different heights).

            ⚠️ Not `size="small"` either — §60 forbids it outright.

            `UserPlusIcon`, the same glyph Invite carries, because it is the same act: letting
            a person in. Two buttons in one slot on two tabs, one with an icon and one without,
            reads as a missing icon rather than as a distinction.
          */
          <Button
            variant="primary"
            disableElevation
            onClick={openAddAllowlist}
            startIcon={<UserPlusIcon strokeWidth={1} />}
          >
            Add to allowlist
          </Button>
        )}
        sx={{ mb: `${TABS_CONTENT_GAP}px` }}
      />

      {tab === 'invites' ? (
        <SiteInvitesTab createOpen={isCreateInviteOpen} onCloseCreate={closeCreateInvite} />
      ) : tab === 'waitlist' ? (
        <Table
          loading={isSignupsLoading}
          columns={signupsColumns}
          rows={signups}
          alwaysShowRowSelection={false}
          bulkActions={getSignupsBulkActions}
          // VRG-545 — expand a row to see what the marketing site actually sent, so a
          // reviewer can qualify a lead without leaving the queue. Returns null for a
          // row with neither submission, which is what suppresses the chevron entirely
          // on admin-created rows (Table's toggle-column override renders nothing for
          // null content). Height is left to Table's `() => 'auto'` default.
          fillBottom
          paginationMode="client"
          initialPageSize={100}
          emptyState={
            <EmptyState
              title="No signups yet"
              text="Signups from the marketing site will appear here."
              icon={<UserPlusIcon strokeWidth={1} size={32} />}
            />
          }
        />
      ) : (
        <Table
          loading={isAllowlistLoading}
          columns={allowlistColumns}
          rows={entries}
          alwaysShowRowSelection={false}
          // VRG-545 — same items as the row kebab, minus the two that can only act on one
          // address (see `allowlistActions`'s `bulkSafe`). Sending twenty people their
          // signup link one kebab at a time was the actual cost this closes.
          bulkActions={getAllowlistBulkActions}
          fillBottom
          paginationMode="client"
          initialPageSize={100}
          emptyState={
            <EmptyState
              title="Allowlist is empty"
              text="Add the emails permitted to self-sign-up before public launch."
              icon={<UserPlusIcon strokeWidth={1} size={32} />}
            />
          }
        />
      )}

      <GrantSignupModal open={isGrantModalOpen} onClose={closeGrantModal} />

      {/*
        The Allowlist tab's add control.

        ⚠️ `signups` is passed DOWN rather than re-queried inside the modal. `useSignups()`
        would dedupe on the same key, but a second instance is a second place the args can
        drift, and the two would then be different queries rendering the same table.
      */}
      <AddToAllowlistModal
        open={isAddAllowlistOpen}
        onClose={closeAddAllowlist}
        waitlist={signups}
        isWaitlistLoading={isSignupsLoading}
        existingEmails={allowlistEmails}
        onSubmit={handleAddAllowlist}
        isSubmitting={isAdding}
      />

      {/*
        VRG-Pricing.Access.Gate — the three gates.

        Two of them carry an `EmailPreview`, and which two is decided by ONE
        question: does this action send a message? Grant pricing access sends
        `pricing-access-link`; Allow & invite sends `signup-invitation` (verified
        in `GrantSignupUseCase` — `invite: true` + the default `link` mode calls
        `sendSignupInvitation`). "Allow" alone sends NOTHING: it allowlists the
        address and stamps the row, and the use case's `if (invite)` block is
        the only place an email appears. So its gate states the consequence in
        words, because previewing a message that does not exist would be a
        fabrication of exactly the kind this gate exists to prevent.

        BOTH HALVES of each preview resolve from `packages/emails`' registry, so
        an edit to a template lands here with no second copy to keep in step
        (§64). The body renders through `GET /internal/labs/emails/:id/preview` —
        the same `renderEmail` the send uses. The subject comes from the registry
        index via `useEmailTemplateSubject`.

        ⚠️ That second sentence is new, and the claim above it was FALSE without
        it. This comment previously asserted "no second copy to keep in step"
        while `EmailGatePreview` took a hardcoded `subject` prop, which is a
        second copy and duly drifted: the delivered subject became "You have been
        granted access to view Vera pricing" and the modal went on previewing
        "Your Vera pricing". A comment that describes the body and speaks for the
        whole message is how that went unnoticed.

        What the preview cannot show is the LINK: the magic token is minted
        server-side at send time and `pricing-access-link`'s registry entry
        deliberately samples a dead one. The copy under each preview says so
        rather than letting a placeholder pass as the real address.
      */}
      <ConfirmSettingModal
        open={!!pricingGrantTarget}
        onClose={() => setPricingGrantTarget(null)}
        onConfirm={() => {
          if (pricingGrantTarget) grantPricingAccess({ signupId: pricingGrantTarget.id })
          setPricingGrantTarget(null)
        }}
        title="Grant pricing access?"
        consequence={
          <>
            This emails <strong>{pricingGrantTarget?.email}</strong> a single-use link that opens
            Vera&apos;s real price list. The link works once, expires in 7 days, and cannot be
            recalled after it is sent.
          </>
        }
        confirmLabel="Grant pricing access"
        sx={EMAIL_GATE_MODAL_SX}
      >
        <EmailGatePreview
          templateId="pricing-access-link"
          note="The link below is a placeholder. The real one is generated when you grant, and is unique to this recipient."
        />
      </ConfirmSettingModal>

      <ConfirmSettingModal
        open={!!allowTarget}
        onClose={() => setAllowTarget(null)}
        onConfirm={() => {
          if (allowTarget) grantSignup({ signupId: allowTarget.id, invite: false })
          setAllowTarget(null)
        }}
        title="Allow this person to sign up?"
        /*
          TWO effects, and the second is the one a reader would not guess.
          `GrantSignupUseCase` allowlists the address AND stamps pricing access,
          in that order (987c2bf89 — "Allow grants pricing access, one way only").
          A gate that named only the allowlist half would leave the pricing
          column flipping to Granted for a reason the operator was never told,
          which is exactly the silent-consequence case §43 exists for.

          "No email is sent" survives that addition and is checked, not assumed:
          the invite email is inside `if (invite)`, which is false here, and the
          pricing implication deliberately calls the REPOSITORY rather than
          `GrantPricingAccessUseCase` precisely so it does not send the 7-day
          magic link. Two grants, zero messages.
        */
        consequence={
          <>
            <strong>{allowTarget?.email}</strong> is added to the signup allowlist and can create a
            Vera account from that moment on. This also grants them pricing access, so they can see
            Vera&apos;s price list once they sign in. <strong>No email is sent.</strong> They will
            not know unless you tell them, or unless you use &quot;Allow &amp; invite&quot; instead.
          </>
        }
        confirmLabel="Allow"
      />

      <ConfirmSettingModal
        open={!!inviteTarget}
        onClose={() => setInviteTarget(null)}
        onConfirm={() => {
          if (inviteTarget) grantSignup({ signupId: inviteTarget.id, invite: true })
          setInviteTarget(null)
        }}
        title="Allow and send the signup link?"
        // Everything Allow does (including the pricing-access stamp — same
        // `GrantSignupUseCase` path, see the gate above), plus the one thing it
        // does not: a message leaves the building. That ordering is deliberate;
        // the irreversible half goes last because it is the half being confirmed.
        consequence={
          <>
            This adds <strong>{inviteTarget?.email}</strong> to the signup allowlist, grants them
            pricing access, and emails them a link to create their Vera account. The email goes out
            immediately and cannot be recalled.
          </>
        }
        confirmLabel="Allow & invite"
        sx={EMAIL_GATE_MODAL_SX}
      >
        <EmailGatePreview
          templateId="signup-invitation"
          note="The signup link below is a placeholder. The real one points at this environment's platform."
        />
      </ConfirmSettingModal>

      {/*
        THE BULK GATES.

        ## Why these carry NO email preview, and the single-row gates above do

        A judgement call, so here is the argument. The two gates answer different
        questions. A single-row gate asks "what does this message SAY?", and the honest
        answer is the message, which is why it renders one. A bulk gate asks "am I about
        to mail FOURTEEN people?" — the risk being confirmed is scale and audience, not
        content, and the operator has already been able to read this exact template from
        the row kebab one click away. Nothing is hidden by omitting it.

        Against including it: one rendered email beside "and 13 others" implies the
        message VARIES per recipient. For `signup-invitation` and `pricing-access-link`
        the only per-person element is a link the preview deliberately cannot show, so a
        preview here would answer a question nobody asked while quietly suggesting a
        personalisation that does not exist. It also costs the 320px the recipient list
        needs, and pushes the confirm button toward the fold at a page-sized selection.

        So the space goes to `RecipientList` instead: the count in the title, the effect
        in the consequence, and the addresses themselves underneath — because the bulk
        mistake worth catching is a wrong row in the selection, which a count cannot
        reveal and a list can.
      */}
      {/*
        BULK — Grant pricing access. Same shape as the two below it: count in the
        title, effect in the consequence, addresses underneath, no email preview (the
        argument for that omission is the block immediately above).

        The PREAMBLE is what this gate has and the others do not, and it is not
        decoration. `ConfirmSettingModal`'s own docblock reserves that slot for "a fact
        the reader needs in order to weigh the consequence, rather than a second
        consequence", which is exactly the skipped-rows notice: it does not describe
        what confirming will do, it reconciles the number in the title against the
        number of rows the operator ticked. Putting it in the consequence Alert instead
        would read as a second warning of equal weight to "these emails cannot be
        recalled", which it is not.
      */}
      <ConfirmSettingModal
        open={!!bulkPricingGrantTarget?.rows.length}
        onClose={() => setBulkPricingGrantTarget(null)}
        onConfirm={() => {
          bulkPricingGrantTarget?.rows.forEach((row) => grantPricingAccess({ signupId: row.id }))
          setBulkPricingGrantTarget(null)
        }}
        title={`Grant pricing access to ${countPhrase(bulkPricingGrantTarget?.rows.length ?? 0)}?`}
        preamble={
          bulkPricingGrantTarget?.skipped ? (
            <>
              {bulkPricingGrantTarget.skipped} of the{' '}
              {bulkPricingGrantTarget.rows.length + bulkPricingGrantTarget.skipped} selected{' '}
              {bulkPricingGrantTarget.skipped === 1 ? 'is' : 'are'} already allowed to sign up, which
              includes pricing access.{' '}
              {bulkPricingGrantTarget.skipped === 1 ? 'That row is' : 'Those rows are'} not included
              below and will not be emailed.
            </>
          ) : undefined
        }
        // Same two facts the single-row gate names, counted: what the email contains,
        // and that it cannot be taken back. "Each" rather than "they" in the plural,
        // because the per-person link is what makes the count mean anything.
        consequence={
          <>
            This emails {countPhrase(bulkPricingGrantTarget?.rows.length ?? 0)} a single-use link
            that opens Vera&apos;s real price list.{' '}
            {bulkPricingGrantTarget?.rows.length === 1 ? 'The link works' : 'Each link works'} once
            and expires in 7 days.{' '}
            <strong>
              {bulkPricingGrantTarget?.rows.length === 1 ? 'It goes' : 'They go'} out immediately and
              cannot be recalled.
            </strong>
          </>
        }
        confirmLabel="Grant pricing access"
      >
        <RecipientList emails={(bulkPricingGrantTarget?.rows ?? []).map((row) => row.email)} />
      </ConfirmSettingModal>

      <ConfirmSettingModal
        open={!!bulkAllowTarget?.length}
        onClose={() => setBulkAllowTarget(null)}
        onConfirm={() => {
          bulkAllowTarget?.forEach((row) => grantSignup({ signupId: row.id, invite: false }))
          setBulkAllowTarget(null)
        }}
        title={`Allow ${countPhrase(bulkAllowTarget?.length ?? 0)} to sign up?`}
        // Same two effects the single-row Allow names, counted. The pricing-access half
        // is still the one a reader would not guess, so it stays explicit rather than
        // being compressed to make room for the number.
        consequence={
          <>
            {countPhrase(bulkAllowTarget?.length ?? 0, 'This person', 'people')}{' '}
            {bulkAllowTarget?.length === 1 ? 'is' : 'are'} added to the signup allowlist and can
            create a Vera account from that moment on. This also grants pricing access, so they can
            see Vera&apos;s price list once they sign in. <strong>No email is sent.</strong>
          </>
        }
        confirmLabel="Allow"
      >
        <RecipientList emails={(bulkAllowTarget ?? []).map((row) => row.email)} />
      </ConfirmSettingModal>

      <ConfirmSettingModal
        open={!!bulkInviteTarget?.length}
        onClose={() => setBulkInviteTarget(null)}
        onConfirm={() => {
          bulkInviteTarget?.forEach((row) => grantSignup({ signupId: row.id, invite: true }))
          setBulkInviteTarget(null)
        }}
        title={`Allow and invite ${countPhrase(bulkInviteTarget?.length ?? 0)}?`}
        // "Each receives" rather than "they receive": the per-person fact is what makes
        // the count mean something, and it is the sentence that distinguishes this gate
        // from the Allow one directly above it.
        consequence={
          <>
            This adds {countPhrase(bulkInviteTarget?.length ?? 0)} to the signup allowlist and grants{' '}
            {bulkInviteTarget?.length === 1 ? 'them' : 'each of them'} pricing access.{' '}
            <strong>
              {bulkInviteTarget?.length === 1
                ? 'They receive an email with a link to create their Vera account.'
                : `Each of the ${bulkInviteTarget?.length} receives an email with a link to create their Vera account.`}
            </strong>{' '}
            {bulkInviteTarget?.length === 1 ? 'It goes' : 'They go'} out immediately and cannot be
            recalled.
          </>
        }
        confirmLabel="Allow & invite"
      >
        <RecipientList emails={(bulkInviteTarget ?? []).map((row) => row.email)} />
      </ConfirmSettingModal>

      {/*
        ALLOWLIST — "Send signup link". One modal for the row kebab and the bulk bar, so
        the two cannot describe the same action differently. `countPhrase` supplies the
        singular, so a one-address open reads as a sentence about a person rather than
        about a quantity of one.
      */}
      <ConfirmSettingModal
        open={!!sendLinkTarget?.length}
        onClose={() => setSendLinkTarget(null)}
        onConfirm={() => {
          sendLinkTarget?.forEach((email) => inviteByEmail({ email, invite: true }))
          setSendLinkTarget(null)
        }}
        title={
          sendLinkTarget?.length === 1
            ? 'Send the signup link?'
            : `Send the signup link to ${countPhrase(sendLinkTarget?.length ?? 0)}?`
        }
        consequence={
          <>
            {sendLinkTarget?.length === 1
              ? 'This emails them a link to create their Vera account.'
              : `Each of the ${sendLinkTarget?.length ?? 0} receives an email with a link to create their Vera account.`}{' '}
            They are already allowlisted, so this grants nothing new and only sends the link again.
            The {sendLinkTarget?.length === 1 ? 'email goes' : 'emails go'} out immediately and
            cannot be recalled.
          </>
        }
        confirmLabel={sendLinkTarget?.length === 1 ? 'Send signup link' : 'Send signup links'}
        loading={isInviting}
        // The PREVIEW is shown only for a single recipient, on the same reasoning as the
        // bulk gates above: one person, one message, so "what does it say" is the live
        // question. The wider box comes with it and is not paid for otherwise.
        sx={sendLinkTarget?.length === 1 ? EMAIL_GATE_MODAL_SX : undefined}
      >
        {sendLinkTarget?.length === 1 ? (
          <EmailGatePreview
            templateId="signup-invitation"
            note="The signup link below is a placeholder. The real one points at this environment's platform."
          />
        ) : (
          <RecipientList emails={sendLinkTarget ?? []} />
        )}
      </ConfirmSettingModal>

      {/*
        ALLOWLIST — "Remove". Destructive, so the confirm button is red and filled (§32's
        transient/committed tier: the user has reached the point of no return).
      */}
      <ConfirmSettingModal
        open={!!removeAllowlistTarget?.length}
        onClose={() => setRemoveAllowlistTarget(null)}
        onConfirm={() => {
          removeAllowlistTarget?.forEach((email) => removeAllowlist(email))
          setRemoveAllowlistTarget(null)
        }}
        title={
          removeAllowlistTarget?.length === 1
            ? 'Remove this address from the allowlist?'
            : `Remove ${countPhrase(removeAllowlistTarget?.length ?? 0, 'this address', 'addresses')} from the allowlist?`
        }
        // Names the CAPABILITY that is withdrawn, not the row that disappears. "Removes a
        // row" is what the code does; "can no longer create a Vera account" is what it
        // costs, which is the distinction §43 asks the consequence to carry.
        consequence={
          <>
            {removeAllowlistTarget?.length === 1 ? 'This address' : `These ${removeAllowlistTarget?.length ?? 0} addresses`}{' '}
            can no longer create a Vera account. Anyone who has already signed up keeps their
            account. <strong>No email is sent</strong>, so they will not be told. Re-add the address
            to undo this.
          </>
        }
        confirmLabel={removeAllowlistTarget?.length === 1 ? 'Remove' : 'Remove all'}
        destructive
      >
        <RecipientList emails={removeAllowlistTarget ?? []} />
      </ConfirmSettingModal>

      {/* VRG-247 / VRG-545: stages the comp/grant against an ALLOWLIST email —
          no company exists yet, so these target the address, not a company. The
          API resolves the signups row behind it (creating a granted one if the
          email was never on the waitlist), which is why `refetchSignups` is the
          refetch: the Waitlist tab is the surface that can change here, not the
          Allowlist, which shows no grant state. */}
      <CompSubscriptionModal
        email={compTargetEmail ?? undefined}
        open={!!compTargetEmail}
        onClose={() => setCompTargetEmail(null)}
        refetch={refetchSignups}
      />
      <GrantActivationsModal
        email={grantTargetEmail ?? undefined}
        open={!!grantTargetEmail}
        onClose={() => setGrantTargetEmail(null)}
        refetch={refetchSignups}
      />
      </Stack>
    </>
  )
}
